/**
 * RITUAL SERVICE
 * 
 * Manages 7-day initial ritual
 * Tracks ritual completion separately from long-term streak
 * Locks secondary features until ritual is completed
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';

export interface RitualProgress {
  isActive: boolean;
  currentDay: number; // 1-7
  daysRemaining: number;
  isCompleted: boolean;
  startDate: string;
  completedDate?: string;
}

/**
 * Start 7-day ritual
 * Called after onboarding when user selects main objective
 */
export const startRitual = async (userId: string): Promise<void> => {
  const userRef = doc(db, "users", userId);
  const today = new Date().toISOString().split('T')[0];
  
  await updateDoc(userRef, {
    ritualCompleted: false,
    ritualStartDate: today,
    ritualCurrentDay: 1
  });
  
  console.log(`[RITUAL] Started 7-day ritual for user ${userId}`);
};

/**
 * Update ritual progress
 * Called when user completes daily core action
 */
export const updateRitualProgress = async (userId: string): Promise<void> => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) return;
  
  const user = userSnap.data() as User;
  
  if (!user.ritualStartDate || user.ritualCompleted) return;
  
  const startDate = new Date(user.ritualStartDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = today.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 because day 1 is the start date
  
  const currentDay = Math.min(diffDays, 7);
  
  if (currentDay >= 7) {
    // Ritual completed
    await updateDoc(userRef, {
      ritualCompleted: true,
      ritualCurrentDay: 7
    });
    console.log(`[RITUAL] Completed 7-day ritual for user ${userId}`);
  } else {
    await updateDoc(userRef, {
      ritualCurrentDay: currentDay
    });
  }
};

/**
 * Get ritual progress
 */
export const getRitualProgress = (user: User): RitualProgress | null => {
  if (!user.ritualStartDate) {
    return null;
  }
  
  if (user.ritualCompleted) {
    return {
      isActive: false,
      currentDay: 7,
      daysRemaining: 0,
      isCompleted: true,
      startDate: user.ritualStartDate,
      completedDate: user.ritualStartDate // TODO: Store actual completion date
    };
  }
  
  const startDate = new Date(user.ritualStartDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = today.getTime() - startDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  
  const currentDay = Math.min(diffDays, 7);
  const daysRemaining = Math.max(0, 7 - currentDay);
  
  return {
    isActive: true,
    currentDay,
    daysRemaining,
    isCompleted: false,
    startDate: user.ritualStartDate
  };
};

/**
 * Check if ritual is completed (unlocks secondary features)
 */
export const isRitualCompleted = (user: User): boolean => {
  return user.ritualCompleted === true;
};

/**
 * Reset ritual (if user fails)
 * Clean restart without shame copy
 */
export const resetRitual = async (userId: string): Promise<void> => {
  await startRitual(userId);
  console.log(`[RITUAL] Reset ritual for user ${userId}`);
};

